"use client";

import { useEffect, useState, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { useLenis } from "@/hooks/useLenis";
import { useHomePageFontSize } from "./hooks/useHomePageFontSize";
import { SimpleHeader } from "./Layout/SimpleHeader";
import { CursorComponent } from "./Layout/CursorComponent";
import { InfoOverlay } from "./Layout/InfoOverlay";
import { ChromeBrowserSection } from "./Sections/ChromeBrowserSection";
import { ChromeBrowserBackground } from "./Layout/ChromeBrowserBackground";
import { StepListSection } from "./Sections/StepListSection";
import { DeepSearchSection } from "./Sections/DeepSearchSection";
import { BrowserUseSection } from "./Sections/BrowserUseSection";
import { ComputerUseSection } from "./Sections/ComputerUseSection";
import { ControllableSection } from "./Sections/ControllableSection";
import { AgenticMemoryScrollSection } from "./Sections/AgenticMemoryScrollSection";
import { DeepActionSection } from "./Sections/DeepActionSection";
import UserCaseSection from "./Sections/UserCaseSection";
import FAQ from "./Sections/FAQ";
import { StoriesSection } from "./Sections/StoriesSection";
import { MissionSection } from "./Sections/MissionSection";
import { Footer } from "./Layout/Footer";

export default function HomePage() {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const contentAreaRef = useRef<HTMLDivElement>(null);
  const [isAtTop, setIsAtTop] = useState(true);
  const [showHeader, setShowHeader] = useState(false);

  // Reset font-size for HomePage to ensure proper Tailwind rendering
  useHomePageFontSize();

  // Initialize Lenis smooth scrolling
  useLenis({
    duration: 1.5,
    easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    mouseMultiplier: 1.2,
    touchMultiplier: 2,
    normalizeWheel: true,
  });

  useEffect(() => {
    const updateSize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  // Toggle header blur when not at the very top (match use-cases logic)
  useEffect(() => {
    const onScroll = () => setIsAtTop(window.scrollY === 0);
    
    // 立即检查一次
    onScroll();
    
    // 延迟添加监听器，确保与 Lenis 协调
    const timeoutId = setTimeout(() => {
      window.addEventListener("scroll", onScroll, { passive: true });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const handleDownloadClick = () => {
    setIsDownloadModalOpen(true);
  };

  const closeModal = () => {
    setIsDownloadModalOpen(false);
  };

  return (
    <>
      <ChromeBrowserBackground />
      <div className="min-h-screen z-10 relative ">
        <CursorComponent />

        <ChromeBrowserSection onDownloadClick={handleDownloadClick} onShowHeaderChange={setShowHeader} />

        {/* Fixed Header - show when scroll progress > 0.1 */}
        {showHeader && (
          <div
            className={`fixed top-0 left-0 right-0 z-50 py-2 transition-all duration-300 ${
              isAtTop ? "bg-transparent" : "backdrop-blur-[12px]"
            }`}
          >
            <SimpleHeader />
          </div>
        )}

        {/* Content that overlays the browser - higher z-index */}
        <div ref={contentAreaRef} className="relative z-20">
          <UserCaseSection onDownloadClick={handleDownloadClick} />

          <DeepActionSection />

          <StepListSection onDownloadClick={handleDownloadClick} />

          <DeepSearchSection onDownloadClick={handleDownloadClick} />

          <BrowserUseSection onDownloadClick={handleDownloadClick} />

          <ComputerUseSection onDownloadClick={handleDownloadClick} />

          <ControllableSection onDownloadClick={handleDownloadClick} />

          <AgenticMemoryScrollSection onDownloadClick={handleDownloadClick} />

          <FAQ />

          <StoriesSection onDownloadClick={handleDownloadClick} />

          <MissionSection onDownloadClick={handleDownloadClick} />

          <Footer onDownloadClick={handleDownloadClick} />
        </div>

        <AnimatePresence>
          {isDownloadModalOpen && (
            <InfoOverlay 
              close={closeModal} 
              size={size}
              still=""
              fellou="" 
              variant="download"
            />
          )}
        </AnimatePresence>
      </div>

      <style jsx global>{`
        /* 确保 sticky 定位正常工作的样式重置 */
        html, body {
          overflow-x: visible !important;
          overflow-y: visible !important;
          overflow: visible !important;
        }

        /* 移除可能干扰 sticky 的容器样式 */
        .fellou-layout-content {
          overflow: visible !important;
        }

        .overlay-root {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          z-index: 1000;
        }

        .overlay-content {
          background: rgb(147, 51, 234, 0.2);
          backdrop-filter: blur(3px);
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;
          will-change: opacity;
        }

        .download-confirmation {
          width: 75%;
          max-width: 600px;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 30px;
          will-change: transform;
        }

        .download-confirmation header {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 5px;
          text-align: center;
        }

        .download-confirmation h2 {
          color: #f5f5f5;
          margin: 0;
        }

        .download-confirmation p {
          color: #f5f5f5;
          margin: 0;
        }

        .controls {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 10px;
        }

        button.platform-btn {
          background-color: #f5f5f5;
          color: #0f1115;
          border-radius: 20px;
          padding: 12px 24px;
          border: none;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          width: 300px;
          display: flex;
          align-items: center;
          gap: 12px;
          justify-content: flex-start;
        }

        button.platform-btn:hover:not(:disabled) {
          background-color: #e0e0e0;
        }

        button.platform-btn-disabled {
          background-color: #f0f0f0;
          color: #999;
          cursor: not-allowed;
        }

        button.platform-btn-disabled:hover {
          background-color: #f0f0f0;
        }

        .platform-icon {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }

        button.cancel {
          background: none;
          color: #f5f5f5;
          border: none;
          padding: 10px 20px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.2s;
        }

        button.cancel:hover {
          opacity: 0.7;
        }

        .gradient-container {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1000;
          pointer-events: none;
        }

        .expanding-circle {
          position: absolute;
          border-radius: 50%;
          background: rgb(196, 181, 253, 0.8);
          filter: blur(15px);
          transform-origin: center;
          will-change: transform;
        }

        .gradient-circle {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          aspect-ratio: 1;
          will-change: transform;
        }

        .top-left {
          background: rgb(147, 51, 234, 0.9);
        }

        .bottom-right {
          background: rgb(168, 85, 247, 0.9);
        }

        @media (min-aspect-ratio: 1/1) {
          .gradient-circle {
            filter: blur(120px);
          }

          .top-left {
            background: rgb(147, 51, 234, 0.8);
          }

          .bottom-right {
            background: rgb(168, 85, 247, 0.8);
          }
        }
      `}</style>
    </>
  );
}
